import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Divider,
  Chip,
  IconButton,
} from '@mui/material';
import { Add, Close } from '@mui/icons-material';
import { FieldMappingItem } from '../../models';

interface FieldEditDialogProps {
  open: boolean;
  field: FieldMappingItem | null;
  onSave: (field: FieldMappingItem) => void;
  onClose: () => void;
  showOcrOptions?: boolean;
}

export const FieldEditDialog: React.FC<FieldEditDialogProps> = ({
  open,
  field,
  onSave,
  onClose,
  showOcrOptions = false,
}) => {
  const [formData, setFormData] = useState<FieldMappingItem>({
    field_id: '',
    hwpx_field: '',
    label: '',
    type: 'text',
    required: false,
    max_length: undefined,
    placeholder: '',
    options: [],
    position: { top: 10, left: 10, width: 20, height: 4 },
    bbox: undefined,
    ocr_enabled: false,
    form_enabled: true,
  });
  const [newOption, setNewOption] = useState('');

  useEffect(() => {
    if (field) {
      setFormData({
        ...field,
        options: field.options || [],
        position: field.position || { top: 10, left: 10, width: 20, height: 4 },
        bbox: field.bbox,
        ocr_enabled: field.ocr_enabled ?? false,
        form_enabled: field.form_enabled ?? true,
      });
    } else {
      setFormData({
        field_id: '',
        hwpx_field: '',
        label: '',
        type: 'text',
        required: false,
        max_length: undefined,
        placeholder: '',
        options: [],
        position: { top: 10, left: 10, width: 20, height: 4 },
        bbox: undefined,
        ocr_enabled: false,
        form_enabled: true,
      });
    }
  }, [field, open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { name: string; value: unknown } }) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handlePositionChange = (key: 'top' | 'left' | 'width' | 'height', value: string) => {
    const numValue = parseFloat(value) || 0;
    setFormData((prev) => ({
      ...prev,
      position: {
        ...prev.position!,
        [key]: numValue,
      },
    }));
  };

  const handleAddOption = () => {
    if (newOption.trim()) {
      setFormData((prev) => ({
        ...prev,
        options: [...(prev.options || []), newOption.trim()],
      }));
      setNewOption('');
    }
  };

  const handleRemoveOption = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      options: prev.options?.filter((_, i) => i !== index),
    }));
  };

  const handleSave = () => {
    if (!formData.field_id || !formData.label) {
      return;
    }
    onSave(formData);
  };

  const isNewField = !field || !field.field_id;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isNewField ? '필드 추가' : '필드 편집'}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {/* 기본 정보 */}
          <Typography variant="subtitle2" color="primary">
            기본 정보
          </Typography>

          <TextField
            name="field_id"
            label="필드 ID"
            value={formData.field_id}
            onChange={handleChange}
            fullWidth
            required
            size="small"
            disabled={!isNewField}
            helperText="영문, 숫자, 언더스코어만 사용 (예: client_name)"
          />

          <TextField
            name="label"
            label="레이블"
            value={formData.label}
            onChange={handleChange}
            fullWidth
            required
            size="small"
            helperText="화면에 표시될 필드 이름"
          />

          <TextField
            name="hwpx_field"
            label="HWPX 필드명"
            value={formData.hwpx_field}
            onChange={handleChange}
            fullWidth
            size="small"
            helperText="HWPX 템플릿에서 치환될 필드명 (선택)"
          />

          <FormControl fullWidth size="small">
            <InputLabel>필드 타입</InputLabel>
            <Select
              name="type"
              value={formData.type}
              label="필드 타입"
              onChange={(e) => handleChange({ target: { name: 'type', value: e.target.value } })}
            >
              <MenuItem value="text">텍스트</MenuItem>
              <MenuItem value="textarea">여러 줄 텍스트</MenuItem>
              <MenuItem value="number">숫자</MenuItem>
              <MenuItem value="date">날짜</MenuItem>
              <MenuItem value="select">선택</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  name="required"
                  checked={formData.required || false}
                  onChange={handleCheckboxChange}
                />
              }
              label="필수 입력"
            />

            {formData.type === 'text' && (
              <TextField
                name="max_length"
                label="최대 길이"
                type="number"
                value={formData.max_length || ''}
                onChange={handleChange}
                size="small"
                sx={{ width: 120 }}
              />
            )}
          </Box>

          <TextField
            name="placeholder"
            label="플레이스홀더"
            value={formData.placeholder || ''}
            onChange={handleChange}
            fullWidth
            size="small"
            helperText="입력란에 표시될 힌트 텍스트"
          />

          {/* 선택 옵션 (select 타입일 때만) */}
          {formData.type === 'select' && (
            <>
              <Divider />
              <Typography variant="subtitle2" color="primary">
                선택 옵션
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  value={newOption}
                  onChange={(e) => setNewOption(e.target.value)}
                  placeholder="옵션 추가"
                  size="small"
                  fullWidth
                  onKeyPress={(e) => e.key === 'Enter' && handleAddOption()}
                />
                <IconButton onClick={handleAddOption} color="primary">
                  <Add />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {formData.options?.map((option, index) => (
                  <Chip
                    key={index}
                    label={option}
                    onDelete={() => handleRemoveOption(index)}
                    size="small"
                  />
                ))}
                {(!formData.options || formData.options.length === 0) && (
                  <Typography variant="body2" color="text.secondary">
                    옵션이 없습니다.
                  </Typography>
                )}
              </Box>
            </>
          )}

          {/* OCR/Form 옵션 (showOcrOptions일 때만) */}
          {showOcrOptions && (
            <>
              <Divider />
              <Typography variant="subtitle2" color="primary">
                용도 설정
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="form_enabled"
                      checked={formData.form_enabled ?? true}
                      onChange={handleCheckboxChange}
                    />
                  }
                  label="폼 입력 필드"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      name="ocr_enabled"
                      checked={formData.ocr_enabled ?? false}
                      onChange={handleCheckboxChange}
                    />
                  }
                  label="OCR 추출 필드"
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                폼 입력: 상담 기록 편집 시 입력 필드로 표시 / OCR 추출: 스캔 문서에서 텍스트 추출 위치
              </Typography>
            </>
          )}

          {/* 위치 정보 */}
          <Divider />
          <Typography variant="subtitle2" color="primary">
            위치 및 크기 (%)
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="상단"
              type="number"
              value={formData.position?.top || 0}
              onChange={(e) => handlePositionChange('top', e.target.value)}
              size="small"
              inputProps={{ min: 0, max: 100, step: 0.5 }}
            />
            <TextField
              label="좌측"
              type="number"
              value={formData.position?.left || 0}
              onChange={(e) => handlePositionChange('left', e.target.value)}
              size="small"
              inputProps={{ min: 0, max: 100, step: 0.5 }}
            />
            <TextField
              label="너비"
              type="number"
              value={formData.position?.width || 0}
              onChange={(e) => handlePositionChange('width', e.target.value)}
              size="small"
              inputProps={{ min: 1, max: 100, step: 0.5 }}
            />
            <TextField
              label="높이"
              type="number"
              value={formData.position?.height || 0}
              onChange={(e) => handlePositionChange('height', e.target.value)}
              size="small"
              inputProps={{ min: 1, max: 100, step: 0.5 }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>취소</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!formData.field_id || !formData.label}
        >
          저장
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FieldEditDialog;
